<template>
  <ServicePageLayout
    icon="fas fa-life-ring"
    title="Support Services"
    subtitle="Comprehensive assistance for your academic journey"
  >
    <section class="content-section">
      <h2>
        <i class="fas fa-info-circle section-icon" />
        Overview
      </h2>
      <p>
        Student Support Services provides a wide range of assistance programs designed to help
        students succeed academically and personally. We offer support for students with special
        needs, academic challenges, financial difficulties, and other concerns that may affect
        their educational experience.
      </p>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-book-open section-icon" />
        Academic Support
      </h2>
      <div class="support-grid">
        <div class="support-card">
          <div class="card-icon green">
            <i class="fas fa-chalkboard-teacher" />
          </div>
          <h3>Peer Tutoring</h3>
          <p>Free tutoring sessions with qualified student tutors for various subjects.</p>
          <span class="availability">Available: Mon-Fri, 9AM-5PM</span>
        </div>
        <div class="support-card">
          <div class="card-icon yellow">
            <i class="fas fa-laptop" />
          </div>
          <h3>Study Skills Workshop</h3>
          <p>Learn effective study techniques, time management, and exam preparation.</p>
          <span class="availability">Monthly sessions</span>
        </div>
        <div class="support-card">
          <div class="card-icon green">
            <i class="fas fa-users" />
          </div>
          <h3>Study Groups</h3>
          <p>Facilitated study groups for challenging courses and exam reviews.</p>
          <span class="availability">As scheduled</span>
        </div>
        <div class="support-card">
          <div class="card-icon yellow">
            <i class="fas fa-file-alt" />
          </div>
          <h3>Writing Assistance</h3>
          <p>Help with research papers, essays, and academic writing.</p>
          <span class="availability">By appointment</span>
        </div>
      </div>
    </section>

    <section class="content-section highlight">
      <h2>
        <i class="fas fa-wheelchair section-icon" />
        Students with Disabilities
      </h2>
      <p>
        We are committed to ensuring equal access and opportunity for students with
        disabilities. Our support services include accommodations and assistance to help
        you succeed in your academic pursuits.
      </p>
      <div class="pwd-services">
        <div class="pwd-item">
          <i class="fas fa-check" />
          <span>Accessible facilities and classrooms</span>
        </div>
        <div class="pwd-item">
          <i class="fas fa-check" />
          <span>Extended time for examinations</span>
        </div>
        <div class="pwd-item">
          <i class="fas fa-check" />
          <span>Note-taking assistance</span>
        </div>
        <div class="pwd-item">
          <i class="fas fa-check" />
          <span>Alternative format materials</span>
        </div>
        <div class="pwd-item">
          <i class="fas fa-check" />
          <span>Priority enrollment assistance</span>
        </div>
        <div class="pwd-item">
          <i class="fas fa-check" />
          <span>Personalized support plans</span>
        </div>
      </div>
      <button
        class="action-btn"
        @click="requestAccommodation"
      >
        <i class="fas fa-clipboard-list" />
        Request Accommodation
      </button>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-hand-holding-usd section-icon" />
        Financial Assistance
      </h2>
      <p>
        We help students navigate financial challenges and connect them with available
        resources to support their education.
      </p>
      <div class="financial-options">
        <div class="fin-card">
          <div class="fin-icon">
            <i class="fas fa-graduation-cap" />
          </div>
          <div class="fin-content">
            <h4>Scholarship Referral</h4>
            <p>
              Information and assistance for internal and external scholarship opportunities.
            </p>
          </div>
        </div>
        <div class="fin-card">
          <div class="fin-icon">
            <i class="fas fa-piggy-bank" />
          </div>
          <div class="fin-content">
            <h4>Emergency Fund</h4>
            <p>
              Limited emergency financial assistance for students facing unexpected hardships.
            </p>
          </div>
        </div>
        <div class="fin-card">
          <div class="fin-icon">
            <i class="fas fa-briefcase" />
          </div>
          <div class="fin-content">
            <h4>Student Employment</h4>
            <p>
              On-campus job opportunities for students who want to earn while they learn.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-laptop section-icon" />
        Technology Support
      </h2>
      <div class="tech-grid">
        <div class="tech-item">
          <i class="fas fa-desktop" />
          <h4>Computer Lab</h4>
          <p>Access to computers with internet and printing services.</p>
        </div>
        <div class="tech-item">
          <i class="fas fa-wifi" />
          <h4>Campus WiFi</h4>
          <p>Free WiFi access throughout the campus.</p>
        </div>
        <div class="tech-item">
          <i class="fas fa-tablet-alt" />
          <h4>Device Lending</h4>
          <p>Tablet/laptop lending for students in need (subject to availability).</p>
        </div>
        <div class="tech-item">
          <i class="fas fa-question-circle" />
          <h4>IT Help Desk</h4>
          <p>Technical support for LMS and school systems.</p>
        </div>
      </div>
    </section>

    <section class="content-section cta">
      <h2>How to Access Support Services</h2>
      <p>
        Getting help is easy. Visit our office or reach out through any of the channels below.
        All services are free for enrolled students.
      </p>
      <div class="access-methods">
        <div class="method">
          <div class="method-icon">
            <i class="fas fa-walking" />
          </div>
          <h4>Walk-In</h4>
          <p>Visit Room 204, Student Services Building</p>
        </div>
        <div class="method">
          <div class="method-icon">
            <i class="fas fa-envelope" />
          </div>
          <h4>Email</h4>
          <p>support@nc.edu.ph</p>
        </div>
        <div class="method">
          <div class="method-icon">
            <i class="fas fa-phone" />
          </div>
          <h4>Call</h4>
          <p>(078) 123-4567 Local 204</p>
        </div>
      </div>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-clock section-icon" />
        Office Hours
      </h2>
      <div class="hours-grid">
        <div class="hours-item">
          <span class="day">Monday - Friday</span>
          <span class="time">8:00 AM - 5:00 PM</span>
        </div>
        <div class="hours-item">
          <span class="day">Saturday</span>
          <span class="time">8:00 AM - 12:00 PM</span>
        </div>
        <div class="hours-item">
          <span class="day">Sunday & Holidays</span>
          <span class="time">Closed</span>
        </div>
      </div>
    </section>
  </ServicePageLayout>
</template>

<script>
import ServicePageLayout from '@/components/landing/ServicePageLayout.vue'

export default {
  name: 'SupportPage',

  components: {
    ServicePageLayout
  },

  metaInfo: () => ({
    title: 'Support Services'
  }),

  methods: {
    requestAccommodation () {
      this.$bvToast.toast('Accommodation request form coming soon!', {
        title: 'Coming Soon',
        variant: 'info',
        solid: true
      })
    }
  }
}
</script>
